(function () {
  'use strict';

  var GIFT_PROPERTY_KEY = '_free_gift';
  var GIFT_PROPERTY_VALUE = 'true';
  var CART_ATTRIBUTE = 'gift_variant_id';
  var DISMISSED_STORAGE_KEY = 'vc:freeGift:dismissed';
  var DEBOUNCE_MS = 50;

  function formatMoney(cents) {
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(cents);
    }
    return '$' + (cents / 100).toFixed(2);
  }

  function findBlock() {
    return document.querySelector('[data-gift-block]');
  }

  function readConfig(block) {
    return {
      thresholdCents: parseInt(block.getAttribute('data-threshold-cents'), 10) || 0,
      productId: block.getAttribute('data-product-id'),
      defaultVariantId: block.getAttribute('data-default-variant-id') || '',
      belowMessageTemplate: block.getAttribute('data-below-message-template') || '',
      unlockedMessage: block.getAttribute('data-unlocked-message') || ''
    };
  }

  function isGiftLine(item) {
    return item && item.properties && item.properties[GIFT_PROPERTY_KEY] === GIFT_PROPERTY_VALUE;
  }

  function subtotalExcludingGift(cart) {
    return cart.items.reduce(function (sum, item) {
      return isGiftLine(item) ? sum : sum + item.final_line_price;
    }, 0);
  }

  function findGiftLine(cart) {
    for (var i = 0; i < cart.items.length; i++) {
      if (isGiftLine(cart.items[i])) return cart.items[i];
    }
    return null;
  }

  function fetchJson(url, options) {
    return fetch(url, options).then(function (res) {
      if (!res.ok) throw new Error('Cart request failed: ' + res.status);
      return res.json();
    });
  }

  function FreeGiftManager() {
    this.busy = false;
    this.pending = false;
    this.lastSelectedVariantId = null;
    this.debounceTimer = null;
  }

  FreeGiftManager.prototype.init = function () {
    var block = findBlock();
    if (!block) return;

    this.attachListeners();
    this.scheduleReconcile();
  };

  FreeGiftManager.prototype.attachListeners = function () {
    var self = this;

    // Event delegation: swatch clicks survive section re-renders.
    document.addEventListener('click', function (event) {
      var swatch = event.target.closest && event.target.closest('[data-free-gift-swatch]');
      if (!swatch) return;
      if (swatch.disabled || swatch.getAttribute('aria-disabled') === 'true') return;
      event.preventDefault();
      self.onSwatchClick(swatch.getAttribute('data-variant-id'));
    });

    // Theme cart events. The Stiletto theme dispatches these on document
    // via dispatchCustomEvent in addition to its internal PubSub.
    document.addEventListener('cart:updated', function () {
      self.scheduleReconcile();
    });
    document.addEventListener('quick-cart:updated', function () {
      self.scheduleReconcile();
    });
  };

  FreeGiftManager.prototype.scheduleReconcile = function () {
    var self = this;
    clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(function () {
      self.reconcile();
    }, DEBOUNCE_MS);
  };

  FreeGiftManager.prototype.reconcile = function () {
    var self = this;
    if (this.busy) {
      this.pending = true;
      return Promise.resolve();
    }
    this.busy = true;

    return fetchJson('/cart.js')
      .then(function (cart) {
        return self.reconcileFromCart(cart);
      })
      .catch(function (err) {
        // eslint-disable-next-line no-console
        if (window.console) console.warn('[free-gift] reconcile failed:', err);
      })
      .then(function () {
        self.busy = false;
        if (self.pending) {
          self.pending = false;
          self.scheduleReconcile();
        }
      });
  };

  FreeGiftManager.prototype.reconcileFromCart = function (cart) {
    var block = findBlock();
    if (!block) return Promise.resolve();
    var config = readConfig(block);

    var subtotal = subtotalExcludingGift(cart);
    var giftLine = findGiftLine(cart);
    var unlocked = subtotal >= config.thresholdCents;
    var selectedVariantId =
      (cart.attributes && cart.attributes[CART_ATTRIBUTE]) ||
      this.lastSelectedVariantId ||
      block.getAttribute('data-selected-variant-id') ||
      config.defaultVariantId;

    this.renderProgress(block, subtotal, config, unlocked);

    if (!unlocked) {
      if (giftLine) {
        return this.removeGift(giftLine.key);
      }
      return Promise.resolve();
    }

    // Unlocked.
    if (this.isDismissed()) {
      return Promise.resolve();
    }

    if (!giftLine) {
      if (!selectedVariantId) return Promise.resolve();
      return this.addGift(selectedVariantId);
    }

    var giftVariantId = String(giftLine.variant_id);
    var wantedVariantId = String(selectedVariantId);
    if (giftVariantId !== wantedVariantId) {
      var self = this;
      return this.removeGift(giftLine.key).then(function () {
        return self.addGift(wantedVariantId);
      });
    }

    if (giftLine.quantity !== 1) {
      return this.updateGiftQuantity(giftLine.key, 1);
    }

    return Promise.resolve();
  };

  FreeGiftManager.prototype.renderProgress = function (block, subtotal, config, unlocked) {
    var bar = block.querySelector('.free-gift-block__bar-progress');
    var message = block.querySelector('.free-gift-block__message');

    if (bar) {
      var percent = config.thresholdCents > 0 ? (subtotal * 100) / config.thresholdCents : 0;
      if (percent > 100) percent = 100;
      bar.style.width = percent + '%';
    }

    if (message) {
      if (unlocked) {
        message.innerHTML = config.unlockedMessage;
        block.classList.add('free-gift-block--unlocked');
      } else {
        var remaining = config.thresholdCents - subtotal;
        if (remaining < 0) remaining = 0;
        message.innerHTML = config.belowMessageTemplate.replace('{{ amount }}', formatMoney(remaining));
        block.classList.remove('free-gift-block--unlocked');
      }
    }
  };

  FreeGiftManager.prototype.addGift = function (variantId) {
    var body = {
      items: [
        {
          id: parseInt(variantId, 10),
          quantity: 1,
          properties: (function () {
            var p = {};
            p[GIFT_PROPERTY_KEY] = GIFT_PROPERTY_VALUE;
            return p;
          })()
        }
      ]
    };
    return fetchJson('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(this.notifyCartChange.bind(this));
  };

  FreeGiftManager.prototype.removeGift = function (lineKey) {
    return fetchJson('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: lineKey, quantity: 0 })
    }).then(this.notifyCartChange.bind(this));
  };

  FreeGiftManager.prototype.updateGiftQuantity = function (lineKey, quantity) {
    return fetchJson('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: lineKey, quantity: quantity })
    }).then(this.notifyCartChange.bind(this));
  };

  FreeGiftManager.prototype.notifyCartChange = function (cart) {
    // Tell the theme to re-render the cart UI. The theme listens for both
    // events and refreshes the drawer / cart icon accordingly.
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cart } }));
    document.dispatchEvent(new CustomEvent('quick-cart:updated', { detail: { cart: cart } }));
    return cart;
  };

  FreeGiftManager.prototype.onSwatchClick = function (variantId) {
    var self = this;
    this.lastSelectedVariantId = variantId;
    this.updateSwatchUI(variantId);

    // Persist the customer's choice as a cart attribute, then reconcile.
    fetchJson('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        attributes: (function () {
          var a = {};
          a[CART_ATTRIBUTE] = variantId;
          return a;
        })()
      })
    })
      .catch(function (err) {
        if (window.console) console.warn('[free-gift] swatch update failed:', err);
      })
      .then(function () {
        self.scheduleReconcile();
      });
  };

  FreeGiftManager.prototype.updateSwatchUI = function (variantId) {
    var swatches = document.querySelectorAll('[data-free-gift-swatch]');
    for (var i = 0; i < swatches.length; i++) {
      var sw = swatches[i];
      var match = sw.getAttribute('data-variant-id') === String(variantId);
      sw.classList.toggle('is-selected', match);
      sw.setAttribute('aria-pressed', match ? 'true' : 'false');
    }
  };

  FreeGiftManager.prototype.isDismissed = function () {
    try {
      return window.sessionStorage.getItem(DISMISSED_STORAGE_KEY) === '1';
    } catch (e) {
      return false;
    }
  };

  FreeGiftManager.prototype.markDismissed = function () {
    try {
      window.sessionStorage.setItem(DISMISSED_STORAGE_KEY, '1');
    } catch (e) {
      /* sessionStorage unavailable */
    }
  };

  function boot() {
    var manager = new FreeGiftManager();
    window.freeGiftManager = manager;
    manager.init();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
